/**
 * Combined Data Hook for Contract Manager
 *
 * This hook provides all the data needed by ContractManager in a single
 * interface, using React Query for caching and automatic refetching.
 * It replaces the manual loadData pattern with declarative data fetching.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contractService, supplierService, contractGroupService, ContractApiResponse, ContractGroup } from '../../services/api';
import { queryKeys, STALE_TIMES } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';
import { useMemo } from 'react';

/**
 * Transformed contract for frontend use
 */
export interface TransformedContract {
  id: string;
  _id?: string;
  supplierId: string;
  supplierName: string;
  contractTitle: string;
  year: number;
  startDate: string;
  endDate: string;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  reviewStatus: 'not_received' | 'pending_review' | 'under_review' | 'finalized';
  description?: string;
  paymentTerms?: string;
  renewalDate?: string;
  attachments?: any[];
  negotiationPoints?: any[];
  minPurchaseAmount?: number;
  marketingBudget?: number;
  renewalTermsCount?: number;
  incotermTerms?: string;
  paymentCurrency?: string;
  groupId?: string;
  groupName?: string;
}

/**
 * Transformed supplier for frontend use
 */
export interface TransformedSupplier {
  id: string;
  companyName: string;
  name: string;
  taxId: string;
  registrationNumber?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactPersons: Array<{
    name: string;
    role: string;
    email: string;
    phone: string;
    isPrimary?: boolean;
  }>;
  category?: string;
  status?: string;
  performanceRating?: number;
  notes?: string;
  registrationDate?: string;
  website?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
}

/**
 * Hook that provides all data needed by ContractManager
 * with automatic caching, refetching, and loading states
 */
export function useContractManagerData() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  // Fetch suppliers
  const suppliersQuery = useQuery({
    queryKey: queryKeys.suppliers.list(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return supplierService.getAll(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.STATIC,
  });

  // Fetch contract groups
  const groupsQuery = useQuery({
    queryKey: [...queryKeys.contractGroups.list(), 'active'],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.getActive(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.STATIC,
  });

  // Fetch contracts
  const contractsQuery = useQuery({
    queryKey: queryKeys.contracts.lists(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractService.getAll(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.CONTRACTS,
  });

  // Transform suppliers data
  const transformedSuppliers = useMemo<TransformedSupplier[]>(() => {
    if (!suppliersQuery.data) return [];

    return suppliersQuery.data.map((supplier: any) => {
      let address = {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States',
      };

      if (supplier.address) {
        if (typeof supplier.address === 'string') {
          const parts = supplier.address.split(',').map((p: string) => p.trim());
          address = {
            street: parts[0] || '',
            city: parts[1] || '',
            state: parts[2] || '',
            zipCode: parts[3] || '',
            country: parts[4] || 'United States',
          };
        } else {
          address = {
            street: supplier.address.street || '',
            city: supplier.address.city || '',
            state: supplier.address.state || '',
            zipCode: supplier.address.zipCode || '',
            country: supplier.address.country || 'United States',
          };
        }
      }

      return {
        id: supplier.id?.toString() || supplier._id,
        companyName: supplier.companyName || supplier.name || '',
        name: supplier.companyName || supplier.name || '',
        taxId: supplier.taxId || '',
        registrationNumber: supplier.registrationNumber,
        address,
        contactPersons: supplier.contactPersons || [
          {
            name: supplier.contactPerson || '',
            role: 'Primary Contact',
            email: supplier.email || '',
            phone: supplier.phone || '',
            isPrimary: true,
          },
        ],
        category: supplier.category || 'Other',
        status: supplier.status || 'active',
        performanceRating: supplier.performanceRating,
        notes: supplier.notes,
        registrationDate: supplier.registrationDate,
        website: supplier.website,
        contactPerson: supplier.contactPersons?.[0]?.name || supplier.contactPerson || '',
        email: supplier.contactPersons?.[0]?.email || supplier.email || '',
        phone: supplier.contactPersons?.[0]?.phone || supplier.phone || '',
      };
    });
  }, [suppliersQuery.data]);

  // Transform contracts data
  const transformedContracts = useMemo<TransformedContract[]>(() => {
    if (!contractsQuery.data) return [];

    const groups = groupsQuery.data || [];

    return contractsQuery.data.map((contract: ContractApiResponse) => {
      // Find supplier name
      let supplierName = '';
      if (contract.supplier?.companyName) {
        supplierName = contract.supplier.companyName;
      } else if (contract.supplier?.name) {
        supplierName = contract.supplier.name;
      } else if (contract.supplierId) {
        const supplier = transformedSuppliers.find(
          (s) => s.id?.toString() === contract.supplierId?.toString()
        );
        supplierName = supplier?.companyName || supplier?.name || '';
      }

      const rawId = contract._id || contract.id?.toString() || '';
      const contractGroupId = (contract as any).groupId?.toString?.() || (contract as any).groupId;
      const group = groups.find(
        (g: ContractGroup) => (g.id || g._id) === contractGroupId
      );

      return {
        id: rawId,
        _id: contract._id || contract.id?.toString(),
        supplierId: (contract.supplierId as any)?.toString?.() || String(contract.supplierId),
        supplierName,
        contractTitle: contract.contractTitle,
        year: contract.year,
        startDate: contract.startDate,
        endDate: contract.endDate,
        status: contract.status,
        reviewStatus: contract.reviewStatus,
        description: contract.description,
        paymentTerms: contract.paymentTerms,
        renewalDate: contract.renewalDate,
        attachments: contract.attachments || [],
        negotiationPoints: contract.negotiationPoints || [],
        minPurchaseAmount: contract.minPurchaseAmount,
        marketingBudget: contract.marketingBudget,
        renewalTermsCount: contract.renewalTermsCount,
        incotermTerms: contract.incotermTerms,
        paymentCurrency: contract.paymentCurrency,
        groupId: contractGroupId,
        groupName: group?.name || '',
      };
    });
  }, [contractsQuery.data, groupsQuery.data, transformedSuppliers]);

  // Combined loading state
  const isLoading =
    suppliersQuery.isLoading || groupsQuery.isLoading || contractsQuery.isLoading;

  // Combined error state
  const error = suppliersQuery.error || groupsQuery.error || contractsQuery.error;

  // Refetch all data
  const refetch = async () => {
    await Promise.all([
      suppliersQuery.refetch(),
      groupsQuery.refetch(),
      contractsQuery.refetch(),
    ]);
  };

  // Invalidate all contract data (useful after mutations)
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.contractGroups.all });
  };

  return {
    // Data
    contracts: transformedContracts,
    suppliers: transformedSuppliers,
    contractGroups: groupsQuery.data || [],

    // Loading states
    isLoading,
    isContractsLoading: contractsQuery.isLoading,
    isSuppliersLoading: suppliersQuery.isLoading,
    isGroupsLoading: groupsQuery.isLoading,

    // Error states
    error,
    contractsError: contractsQuery.error,
    suppliersError: suppliersQuery.error,
    groupsError: groupsQuery.error,

    // Refetch methods
    refetch,
    refetchContracts: contractsQuery.refetch,
    refetchSuppliers: suppliersQuery.refetch,
    refetchGroups: groupsQuery.refetch,

    // Invalidation
    invalidateAll,

    // Query states (for more granular control)
    contractsQuery,
    suppliersQuery,
    groupsQuery,
  };
}

export default useContractManagerData;
